import { useState } from "react";
import { useNavigate } from "react-router-dom";
import "../styles/LandingPage.css";
import "./Patients.css";
import Header from "../components/Header/Header";
import Loading from "../components/Loading";
import Error from "../components/Error";
import NotFound from "../components/NotFound";
import SearchIcon from "../components/Icons/SearchIcon";
import Card from "../components/Card";
import Container from "../components/Container";
import Title from "../components/Title";
import CardHeader from "../components/CardHeader";
import PatientCard from "../components/PatientCard";
import { usePatient } from "../hooks/usePatient";

const PatientSearch = () => {
  const navigate = useNavigate();
  const [phoneNumber, setPhoneNumber] = useState("");
  const [searchTriggered, setSearchTriggered] = useState(false);
  const [selectedSpecialty, setSelectedSpecialty] = useState(null);

  const { data: patient, isLoading, isError, error } = usePatient(phoneNumber, searchTriggered);

  const specialties = ["Cardiology", "Gynaecology", "Neurology", "Dermatology"];

  const handleSpecialtyClick = (specialty) => {
    if (specialty === "Cardiology") {
      navigate("/landing/medical-categories/cardiology");
    } else {
      // For now, just toggle selection for other specialties
      setSelectedSpecialty(specialty === selectedSpecialty ? null : specialty);
    }
  }; 

  const handleSearch = (e) => {
    e.preventDefault();
    if (phoneNumber.trim()) {
      setSearchTriggered(true);
    }
  };

  const handlePhoneChange = (e) => {
    setPhoneNumber(e.target.value);
    if (searchTriggered) {
      setSearchTriggered(false);
    }
  };

  return (
    <>
      <Header
        pageTitle={
          <SearchIcon />
        }
      />

      <div
        className="pb-4 px-4"
        style={{
          height: "calc(100vh - 113px)",
          overflowY: "auto",
          overflowX: "hidden",
        }}
      >
        {/* Search Section */}
        <Container>
          {/* Specialty Buttons */}
          <div className="flex gap-2 mb-4">
            {specialties.map((specialty) => (
              <button
                key={specialty}
                type="button"
                onClick={() => handleSpecialtyClick(specialty)}
                className={`flex-1 py-2 px-3 text-sm font-medium rounded-lg transition-all duration-200 ${
                  selectedSpecialty === specialty
                    ? "bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-md"
                    : "bg-white text-gray-700 border-2 border-gray-200 hover:border-blue-300 hover:shadow-sm"
                }`}
              >
                {specialty}
              </button>
            ))}
          </div>

          <Card className="bg-gradient-to-r from-blue-50 to-indigo-100">
            <CardHeader className="mb-6">
              <Title className="text-gray-800 mb-2">Find Patient</Title>
              <p className="text-gray-600">
                Enter phone number to search for a patient
              </p>
            </CardHeader>

            <form onSubmit={handleSearch} className="flex gap-4">
              <input
                type="tel"
                value={phoneNumber}
                onChange={handlePhoneChange}
                placeholder="Phone Number"
                className="w-full pl-4 py-4 text-lg border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200 transition-all duration-200"
              />

              <button
                type="submit"
                disabled={!phoneNumber.trim() || isLoading}
                className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-2 px-3 rounded-xl font-semibold text-lg hover:from-blue-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 transform hover:scale-[1.02] transition-all duration-200 shadow-lg whitespace-nowrap"
              >
                <div className="flex items-center justify-center">
                  <SearchIcon />
                </div>
              </button>
            </form>
          </Card>
        </Container>

        {/* Results Section */}
        <div className="max-w-4xl mx-auto mt-8">
          {searchTriggered && (
            <>
              {isLoading && <Loading />}

              {isError && (
                <Error
                  error={
                    error?.message ||
                    "Failed to search for patient. Please try again."
                  }
                />
              )}

              {!isLoading && !isError && patient && (
                <div className="space-y-4">
                  <div className="text-center mb-6">
                    <Title className="text-gray-800" size="small">Search Result</Title>
                  </div>
                  <Container size="small">
                    <PatientCard patient={patient} />
                  </Container>
                </div>
              )}

              {!isLoading && !isError && !patient && searchTriggered && (
                <NotFound
                  text={`No patient found with phone number "${phoneNumber}". Please check the number and try again.`}
                />
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default PatientSearch;
